import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import {
  buildCompanyBusinessLinks,
  buildCompanySocialLinks,
  CompanyLink,
} from "@/lib/company-social-links";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { ChevronDownIcon } from "lucide-react";
import { RiGlobalLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";

export default function DataTableCellCompanyLinks({
  company,
}: {
  company: CompanyDTO;
}) {
  const { t } = useTranslation();

  const businessLinks = buildCompanyBusinessLinks(company);
  const socialLinks = buildCompanySocialLinks(company);
  const websiteLink: CompanyLink | null = company.website
    ? {
        icon: RiGlobalLine,
        label: t("components.dataTableCompanies.links.website", {
          domain: company.domain,
        }),
        url: company.website,
        colorClass: "",
      }
    : null;

  const googleMapsLink =
    businessLinks.find((link) => link.label === "Google Maps") ?? null;
  const linkedinLink =
    socialLinks.find((link) =>
      link.url.includes("linkedin.com"),
    ) ?? null;
  const instagramLink =
    socialLinks.find((link) =>
      link.url.includes("instagram.com"),
    ) ?? null;

  const primary = [googleMapsLink, websiteLink, linkedinLink, instagramLink]
    .filter((l): l is CompanyLink => l !== null);
  const primarySet = new Set(primary);

  const linksValid = primary;
  const otherLinksValid = [
    ...businessLinks,
    ...socialLinks,
  ].filter((link) => !primarySet.has(link));

  return (
    <div className="flex flex-row gap-1 justify-between">
      <div className="flex flex-row gap-1">
        {linksValid.map((link) => (
          <Tooltip key={link.label}>
            <TooltipTrigger asChild>
              <Button size="icon" variant="secondary" asChild>
                <Link to={link.url || ""} target="_blank">
                  <link.icon className={link.colorClass} />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{link.label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      <div>
        {otherLinksValid.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="secondary"
                className="pr-2 pl-3 w-auto"
              >
                + {otherLinksValid.length}
                <ChevronDownIcon className="ml-2 w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {otherLinksValid.map((link) => (
                <DropdownMenuItem key={link.label} asChild>
                  <Link
                    to={link.url || ""}
                    target="_blank"
                    className="cursor-pointer"
                  >
                    <link.icon className={cn("mr-2", link.colorClass)} />
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
